import React, { useState, useEffect, useRef } from 'react';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { Link, useNavigate } from 'react-router-dom';

interface SlideProps {
  title: string;
  description: string;
  image: string;
  alt: string;
  link: string;
  packageId: string;
}

const slides: SlideProps[] = [
  {
    title: "Web Development",
    description: "Custom, responsive websites to boost your presence!",
    image: "/images/services/web-development.jpg",
    alt: "Chhindwara Web Development Services",
    link: "/services/web-development",
    packageId: "web-development"
  },
  {
    title: "Social Media Marketing",
    description: "Engage and grow your audience in India!",
    image: "/images/services/social-media.jpg",
    alt: "Social Media Marketing Services India",
    link: "/services/social-media-marketing",
    packageId: "social-media-marketing"
  },
  {
    title: "Graphic Designing",
    description: "Stunning visuals for your brand growth!",
    image: "/images/services/graphic-design.jpg",
    alt: "Graphic Design Services Chhindwara",
    link: "/services/graphic-designing",
    packageId: "graphic-designing"
  },
  {
    title: "SEO Services",
    description: "Rank higher on Google with expert strategies!",
    image: "/images/services/seo.jpg",
    alt: "SEO Services in Chhindwara",
    link: "/services/seo-services",
    packageId: "seo-services"
  },
  {
    title: "WhatsApp Marketing",
    description: "Direct, personalized campaigns for Chhindwara!",
    image: "/images/services/whatsapp.jpg",
    alt: "WhatsApp Marketing Chhindwara",
    link: "/services/whatsapp-marketing",
    packageId: "whatsapp-marketing"
  },
  {
    title: "Advertisement",
    description: "Impactful digital ads for maximum reach!",
    image: "/images/services/advertisement.jpg",
    alt: "Digital Advertisement Services India",
    link: "/services/advertisement",
    packageId: "advertisement"
  },
  {
    title: "Google My Business",
    description: "Optimize your local presence in Chhindwara!",
    image: "/images/services/gmb.jpg",
    alt: "Google My Business Optimization Chhindwara",
    link: "/services/google-my-business",
    packageId: "google-my-business"
  },
  {
    title: "Influencer Marketing",
    description: "Amplify your brand with partnerships!",
    image: "/images/services/influencer.jpg",
    alt: "Influencer Marketing Services India",
    link: "/services/influencer-marketing",
    packageId: "influencer-marketing"
  }
];

export default function HeroSlider() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const sliderRef = useRef<Slider | null>(null);
  const navigate = useNavigate();

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5000,
    beforeChange: (_: any, next: number) => setCurrentSlide(next),
    customPaging: (i: number) => (
      <div
        className={`w-3 h-3 mx-1 rounded-full ${
          i === currentSlide ? 'bg-white' : 'bg-white/50'
        }`}
      />
    ),
    appendDots: (dots: React.ReactNode) => (
      <div>
        <ul className="flex justify-center items-center mb-6"> {dots} </ul>
      </div>
    ),
  };

  // Preload images
  useEffect(() => {
    slides.forEach(slide => {
      const img = new Image();
      img.src = slide.image;
    });
  }, []);

  // Smooth scroll function
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  // Navigate to packages page with specific section
  const navigateToPackage = (packageId: string) => {
    navigate(`/packages#${packageId}`);
  };

  return (
    <div className="relative">
      <Slider ref={sliderRef} {...settings} className="hero-slider">
        {slides.map((slide, index) => (
          <div key={index} className="relative h-[500px] md:h-[600px]">
            <div 
              className="absolute inset-0 bg-cover bg-center"
              style={{ 
                backgroundImage: `url(${slide.image})`,
              }}
            >
              <div className="absolute inset-0 bg-black/50"></div>
            </div>
            <div className="relative h-full flex flex-col justify-center items-center text-center px-4 md:px-8 lg:px-16">
              <div className="animate-fadeIn">
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
                  {slide.title}
                </h2>
                <p className="text-lg md:text-xl text-white mb-8 max-w-2xl mx-auto">
                  {slide.description}
                </p>
                <div className="flex flex-col sm:flex-row justify-center gap-4">
                  <button
                    onClick={() => navigateToPackage(slide.packageId)}
                    className="bg-zolo-blue hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-md transition duration-300 transform hover:scale-105"
                  >
                    View Packages
                  </button>
                  <button
                    onClick={() => navigate('/contact')}
                    className="bg-zolo-orange hover:bg-orange-600 text-white font-bold py-3 px-6 rounded-md transition duration-300 transform hover:scale-105"
                  >
                    Contact Us
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </Slider>

      {/* Custom navigation arrows */}
      <button
        className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 rounded-full p-2 z-10"
        onClick={() => {
          if (sliderRef.current) {
            sliderRef.current.slickPrev();
          }
        }}
        aria-label="Previous slide"
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-white">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
        </svg>
      </button>
      <button
        className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 rounded-full p-2 z-10"
        onClick={() => {
          if (sliderRef.current) {
            sliderRef.current.slickNext();
          }
        }}
        aria-label="Next slide"
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-white">
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
        </svg>
      </button>

      <style>
        {`
        .hero-slider .slick-dots {
          bottom: 20px;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out;
        }
        `}
      </style>
    </div>
  );
}
